import { useState } from 'react'
import { Alert, Anchor, Button, Group, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { Link } from 'react-router-dom'
import { IconAlertTriangle, IconInfoCircle, IconRadar, IconUser } from '@tabler/icons-react'
import { useHandleSignal, useSignalAssignees, useSignalHandlingGate } from '../../hooks/useSignals'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { whenLabel } from '../../utils/format'
import type { SignalHandlingRequest } from '../../services/signalsApi'
import { SignalHandlingCell } from './SignalBits'
import { AssignSignalModal } from './AssignSignalModal'
import {
  HANDLING_KEEPS_OPEN, HANDLING_NOT_RECORDED, handlingAuditLink, handlingControls, handlingErrorView,
  handlingSubjectNote, handlingToast, mergedTimeline, oneClickAssignee, previousOccurrenceText, takeOverConfirmText,
  takeOverLabel,
} from './signalHandlingFormat'
import type { SignalDetail } from '../../types/signals'

/**
 * Who has a signal, and the three things an administrator may do about it (D6b): "I have got this" (acknowledge),
 * "Assign…" (hand it to somebody the audit trail knows), and "Release". There is no resolve and no snooze: nothing
 * here closes or hides a signal — it stays open and counted until the detector sees the condition clear.
 *
 * - The controls render ONLY on the handling gate's `admin` state (the catalogue's server-evaluated
 *   `canHandleSignals`), ONLY on an open signal, and only when its handling was served (handlingControls). A
 *   `denied` gate says why; `pending` and `unknown` show nothing. Every act is bearer-only.
 * - The timeline merges the detector's transitions with the people's acts, by time, detector first on a tie.
 * - The previous occurrence of the same condition says who had it last time; "Assign to X" is one click only when X
 *   is, unambiguously, in the assignee list (oneClickAssignee).
 * - On a halt, and on a critical Data Comb finding, it says what acknowledging the SIGNAL does not do.
 * - An administrator (the read gate) gets the audit trail's view of every act.
 *
 * A refusal stays on screen, worded by its code then its status (handlingErrorView). A HandlingChanged refusal says
 * who acted first, and offers to re-send the same act over it with the fresh revision — after a confirmation.
 */
export function SignalHandlingPanel({ signal, signalHref }: {
  signal: SignalDetail
  /** Where another signal of the Inbox opens — the previous occurrence's link. */
  signalHref: (id: string) => string
}) {
  const gate = useSignalHandlingGate()
  const readGate = useLighthouseAdmin()
  const act = useHandleSignal()
  const [assignOpen, setAssignOpen] = useState(false)
  const [lastRequest, setLastRequest] = useState<SignalHandlingRequest | null>(null)
  const [confirming, setConfirming] = useState(false)

  const controls = handlingControls(gate, signal)
  const note = signal.isOpen ? handlingSubjectNote(signal) : null
  const previous = signal.previousOccurrence ?? null
  const wantsAssignees = controls.assign && previous != null
    && (previous.finalState === 'Assigned' || previous.finalState === 'Acknowledged') && !!previous.finalOwnerName?.trim()
  const assignees = useSignalAssignees(gate, wantsAssignees)
  const oneClick = wantsAssignees ? oneClickAssignee(previous, assignees.data?.items, signal.handling) : null
  const timeline = mergedTimeline(signal)
  const error = act.isError ? handlingErrorView(act.error) : null
  const revision = signal.handling?.revision ?? 0

  const send = (request: SignalHandlingRequest) => {
    setLastRequest(request)
    setConfirming(false)
    act.mutate(request, {
      onSuccess: (result) => {
        const { title, message, color } = handlingToast(request.verb, result)
        notifications.show({ title, message, color })
      },
    })
  }

  return (
    <Stack gap="sm" data-signal-handling-panel="">
      <Stack gap={4}>
        <Text size="sm" fw={600}>Handling</Text>
        <SignalHandlingCell signal={signal} />
        <Text size="xs" c="dimmed">{HANDLING_KEEPS_OPEN}</Text>
      </Stack>

      {(controls.acknowledge || controls.assign || controls.release) && (
        <Group gap="xs" data-signal-handling-controls="">
          {controls.acknowledge && (
            <Button size="xs" loading={act.isPending && lastRequest?.verb === 'acknowledge'} disabled={act.isPending}
              onClick={() => send({ verb: 'acknowledge', id: signal.id, expectedRevision: revision })}>
              I have got this
            </Button>
          )}
          {controls.assign && (
            <Button size="xs" variant="light" disabled={act.isPending} onClick={() => setAssignOpen(true)}>
              Assign…
            </Button>
          )}
          {controls.release && (
            <Button size="xs" variant="subtle" loading={act.isPending && lastRequest?.verb === 'release'} disabled={act.isPending}
              onClick={() => send({ verb: 'release', id: signal.id, expectedRevision: revision })}>
              Release
            </Button>
          )}
        </Group>
      )}

      {controls.deniedReason && (
        <Text size="xs" c="dimmed" data-signal-handling-denied="">{controls.deniedReason}</Text>
      )}

      {error && (
        <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light" title={error.title} data-handling-error={error.kind}>
          <Stack gap={6}>
            <Text size="sm">{error.message}</Text>
            {/* Offered only while acting is still offered at all: on an open signal, behind the admin gate. */}
            {error.kind === 'changed' && error.freshRevision != null && lastRequest && controls.assign && (
              confirming ? (
                <Stack gap={6}>
                  <Text size="sm">{takeOverConfirmText(lastRequest.verb, error.current)}</Text>
                  <Group gap="xs">
                    <Button size="xs" color="red" onClick={() => send({ ...lastRequest, expectedRevision: error.freshRevision! })}>
                      {takeOverLabel(lastRequest.verb)}
                    </Button>
                    <Button size="xs" variant="subtle" onClick={() => setConfirming(false)}>Leave it</Button>
                  </Group>
                </Stack>
              ) : (
                <Group gap="xs">
                  <Button size="xs" variant="light" onClick={() => setConfirming(true)}>{takeOverLabel(lastRequest.verb)}…</Button>
                </Group>
              )
            )}
          </Stack>
        </Alert>
      )}

      {note && (
        <Alert variant="light" color="gray" icon={<IconInfoCircle size={16} />} data-signal-handling-note="">
          <Text size="sm">
            {note.text}{' '}
            <Anchor component={Link} to={note.link.to} size="sm">{note.link.label} →</Anchor>
          </Text>
        </Alert>
      )}

      {previous && (
        <Stack gap={4} data-signal-previous="">
          <Text size="xs" c="dimmed">
            {previousOccurrenceText(previous)}{' '}
            <Anchor component={Link} to={signalHref(previous.id)} size="xs">Open the previous signal</Anchor>
          </Text>
          {oneClick && (
            <Group gap="xs">
              <Button size="xs" variant="light" disabled={act.isPending}
                loading={act.isPending && lastRequest?.verb === 'assign'}
                onClick={() => send({ verb: 'assign', id: signal.id, expectedRevision: revision, assigneeUserId: oneClick.userId })}>
                Assign to {oneClick.name}
              </Button>
              <Text size="xs" c="dimmed">matched by name in the list of people Observatory can name</Text>
            </Group>
          )}
        </Stack>
      )}

      {readGate.state === 'admin' && (
        <Anchor component={Link} to={handlingAuditLink(signal.id)} size="xs" data-signal-audit-link="">
          Every act on this signal in the audit trail →
        </Anchor>
      )}

      <Stack gap={6}>
        <Text size="sm" fw={600}>Timeline</Text>
        <Text size="xs" c="dimmed">
          The detector&apos;s transitions — written on transitions only, so a long-lived condition has a short trail —
          and every person&apos;s act, by time.
          {signal.handlingHistory == null && <> People&apos;s acts are not shown: {HANDLING_NOT_RECORDED}.</>}
        </Text>
        {timeline.length === 0 ? (
          <Text size="sm" c="dimmed">No transition is recorded for this signal.</Text>
        ) : (
          <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 6 }} data-signal-activity="">
            {timeline.map((entry) => (
              <li key={entry.key} data-timeline-source={entry.source}>
                <Group gap={6} wrap="nowrap" align="flex-start">
                  <span style={{ flexShrink: 0, marginTop: 3, color: 'var(--color-text-secondary)', display: 'inline-flex' }}>
                    {entry.source === 'person' ? <IconUser size={13} /> : <IconRadar size={13} />}
                  </span>
                  <Stack gap={0}>
                    <Text size="sm">{entry.text}</Text>
                    <Text size="xs" c="dimmed">
                      {whenLabel(entry.at)}
                      {entry.detail && <> · {entry.detail}</>}
                    </Text>
                  </Stack>
                </Group>
              </li>
            ))}
          </ol>
        )}
      </Stack>

      <AssignSignalModal signal={signal} gate={gate} opened={assignOpen} onClose={() => setAssignOpen(false)} />
    </Stack>
  )
}
